import type { Change, SyncRequest, SyncResponse, SyncTable } from '@timato/shared';
import { db, type OutboxEntry } from '@/db/db';
import { setServerOffset } from '@/lib/clock';
import { hlc } from '@/lib/device';
import { api } from '@/auth/api';
import { getCursor, setCursor, setLastSync, setSyncError, SYNC_META } from './state';

const BATCH = 500;
const tables: Record<SyncTable, { put: (row: never) => Promise<unknown>; get: (id: string) => Promise<{ updatedAt: string } | undefined> }> = {
  settings: { put: (r) => db.settings.put(r), get: () => db.settings.get('settings') },
  activeTimer: { put: (r) => db.activeTimer.put(r), get: () => db.activeTimer.get('active') },
  labels: { put: (r) => db.labels.put(r), get: (id) => db.labels.get(id) },
  projects: { put: (r) => db.projects.put(r), get: (id) => db.projects.get(id) },
  tasks: { put: (r) => db.tasks.put(r), get: (id) => db.tasks.get(id) },
  sessions: { put: (r) => db.sessions.put(r), get: (id) => db.sessions.get(id) },
  agentRuns: { put: (r) => db.agentRuns.put(r), get: (id) => db.agentRuns.get(id) },
};

/** Apply incoming rows with last-write-wins; writes bypass the outbox so nothing echoes back. */
export async function applyIncoming(changes: Change[]): Promise<number> {
  let applied = 0;
  // Every table in `tables` has to be in this transaction, or applying that row throws at runtime.
  await db.transaction('rw', [db.settings, db.activeTimer, db.labels, db.projects, db.tasks, db.sessions, db.agentRuns], async () => {
    for (const c of changes) {
      const t = tables[c.table];
      const current = await t.get(c.rowId);
      if (current && current.updatedAt >= c.updatedAt) continue;
      await t.put(c.payload as never);
      hlc.observe(c.updatedAt);
      applied++;
    }
  });
  return applied;
}

let inFlight: Promise<SyncResult> | null = null;
export type SyncResult = { pushed: number; pulled: number; cursor: number } | { skipped: true };

/** One push/pull round trip (architecture §7). Concurrent callers share the in-flight round. */
export function syncOnce(deviceId: string): Promise<SyncResult> {
  inFlight ??= run(deviceId).finally(() => { inFlight = null; });
  return inFlight;
}

async function run(deviceId: string): Promise<SyncResult> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return { skipped: true };
  const outbox = await db.outbox.orderBy('seq').limit(BATCH).toArray();
  const changes = coalesce(outbox);
  const cursor = await getCursor();
  const sentAt = Date.now();
  try {
    const res = await api<SyncResponse>('/v1/sync', { json: { deviceId, cursor, changes } satisfies SyncRequest });
    const rtt = Date.now() - sentAt;
    setServerOffset(res.serverTime + rtt / 2 - Date.now());
    await db.meta.put({ key: SYNC_META.offset, value: res.serverTime + rtt / 2 - Date.now() });
    const maxSeq = outbox.length ? outbox[outbox.length - 1]!.seq! : null;
    if (maxSeq != null) await db.outbox.where('seq').belowOrEqual(maxSeq).delete();
    const pulled = await applyIncoming(res.changes);
    await setCursor(res.cursor);
    await setLastSync(new Date().toISOString());
    await setSyncError(null);
    const more = outbox.length === BATCH;
    const result = { pushed: changes.length, pulled, cursor: res.cursor };
    return more ? run(deviceId).then((r) => ('skipped' in r ? result : { pushed: result.pushed + r.pushed, pulled: result.pulled + r.pulled, cursor: r.cursor })) : result;
  } catch (e) {
    await setSyncError(e instanceof Error ? e.message : 'sync failed');
    throw e;
  }
}

/** Several local writes to the same row collapse to the latest one before they leave the device. */
export function coalesce(entries: OutboxEntry[]): Change[] {
  const latest = new Map<string, OutboxEntry>();
  for (const e of entries) latest.set(`${e.table}:${e.rowId}`, e);
  return [...latest.values()].map(({ table, rowId, op, payload, updatedAt }) => ({ table, rowId, op, payload, updatedAt }));
}

/** First sign-in with existing local data: queue every row so the account starts with what this device knows (§7). */
export async function enqueueAllLocal() {
  const rows: OutboxEntry[] = [];
  const add = (table: SyncTable, list: { id: string; updatedAt: string }[]) => { for (const r of list) rows.push({ table, rowId: r.id, op: 'upsert', payload: r as unknown as Record<string, unknown>, updatedAt: r.updatedAt }); };
  add('settings', await db.settings.toArray()); add('activeTimer', await db.activeTimer.toArray());
  add('labels', await db.labels.toArray()); add('projects', await db.projects.toArray()); add('tasks', await db.tasks.toArray()); add('sessions', await db.sessions.toArray());
  if (rows.length) await db.outbox.bulkAdd(rows);
  return rows.length;
}
